import { useCallback, useEffect, useState } from "react";
import {
  INITIAL_CAMPAIGN_FORM,
  STORAGE_LAUNCH_KEY,
  SUMMARY_CAMPAIGN_LIMIT,
} from "../constants.js";
import {
  createCologuardCampaign,
  generateCologuardCampaign,
  getCologuardCampaigns,
} from "../services/api.js";
import { generateCampaignDesignator } from "../utils/designator.js";

const ACTIVE_STATUSES = new Set([
  "generation_requested",
  "coupon_generation_queued",
  "generation_in_process",
]);

function readStoredLaunch() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_LAUNCH_KEY)) || null;
  } catch {
    return null;
  }
}

export default function useCampaigns({
  isAuthenticated,
  currentUser,
  accessToken,
  notify,
  onCampaignQueued,
}) {
  const [form, setForm] = useState(INITIAL_CAMPAIGN_FORM);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [launchResult, setLaunchResult] = useState(readStoredLaunch);
  const [campaignState, setCampaignState] = useState({
    loading: false,
    error: "",
    campaigns: [],
    note: "",
  });

  const loadCampaigns = useCallback(async ({ silent = false } = {}) => {
    if (!isAuthenticated || !currentUser?.user_id) return;
    if (!silent) setCampaignState((current) => ({ ...current, loading: true, error: "" }));

    try {
      const result = await getCologuardCampaigns({
        limit: SUMMARY_CAMPAIGN_LIMIT,
        accessToken,
      });
      setCampaignState({
        loading: false,
        error: "",
        campaigns: result.campaigns || [],
        note: result.note || "",
      });
    } catch (error) {
      setCampaignState((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }, [accessToken, currentUser?.user_id, isAuthenticated]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    const hasActiveCampaign = campaignState.campaigns.some((campaign) =>
      ACTIVE_STATUSES.has(String(campaign.campaign_status || ""))
    );
    if (!hasActiveCampaign) return undefined;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") loadCampaigns({ silent: true });
    }, 7000);
    return () => window.clearInterval(intervalId);
  }, [campaignState.campaigns, loadCampaigns]);

  useEffect(() => {
    if (launchResult) {
      window.sessionStorage.setItem(STORAGE_LAUNCH_KEY, JSON.stringify(launchResult));
    }
  }, [launchResult]);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "campaignPrefix" ? value.toUpperCase().replace(/[^A-Z0-9]/g, "") : value,
    }));
  }, []);

  const handleGenerateDesignator = useCallback(() => {
    const generated = generateCampaignDesignator();
    setForm((current) => ({
      ...current,
      campaignPrefix: generated,
      campaignName:
        current.campaignName.trim() === "" || current.campaignName.startsWith("Cologuard - ")
          ? `Cologuard - ${generated}`
          : current.campaignName,
    }));
  }, []);

  const handleLaunchSubmit = useCallback(async (event) => {
    event?.preventDefault?.();
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const creation = await createCologuardCampaign(
        {
          campaign_designator: form.campaignPrefix,
          campaign_name: form.campaignName,
          from_date: form.fromDate,
          to_date: form.toDate,
          requested_code_quantity: Number(form.requestedCodeQuantity),
          create_stage_rule: true,
        },
        { accessToken }
      );
      const campaignId = creation.campaign?.campaign_id;
      if (!campaignId) {
        console.error("Campaign creation response did not include campaign_id", creation);
        throw new Error("Campaign creation did not finish. Please try again.");
      }

      try {
        const generation = await generateCologuardCampaign(
          { campaign_id: campaignId, force_recreate_rule: false },
          { accessToken }
        );
        setLaunchResult({
          campaign: generation.campaign || creation.campaign,
          batchPlan: creation.batchPlan,
          generation,
        });
        setForm(INITIAL_CAMPAIGN_FORM);
        notify(generation.message, "success");
        onCampaignQueued?.();
      } catch (generationError) {
        setLaunchResult({
          campaign: creation.campaign,
          batchPlan: creation.batchPlan,
          generation: {
            error: generationError.message,
            action: "generation_failed",
          },
        });
        setSubmitError(generationError.message);
        notify(generationError.message, "error");
        onCampaignQueued?.();
      }
      await loadCampaigns({ silent: true });
    } catch (error) {
      setSubmitError(error.message);
      notify(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [accessToken, form, loadCampaigns, notify, onCampaignQueued]);

  const handleRetryMagentoGeneration = useCallback(async () => {
    const campaignId = launchResult?.campaign?.campaign_id;
    if (!campaignId) return;
    setIsSubmitting(true);

    try {
      const generation = await generateCologuardCampaign(
        {
          campaign_id: campaignId,
          force_recreate_rule: false,
          retry_failed_batches: true,
        },
        { accessToken }
      );
      setLaunchResult((current) => ({
        ...current,
        campaign: generation.campaign || current.campaign,
        generation,
      }));
      notify(generation.message, "success");
      await loadCampaigns({ silent: true });
    } catch (error) {
      setLaunchResult((current) => ({
        ...current,
        generation: { ...current.generation, error: error.message },
      }));
      notify(error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  }, [accessToken, launchResult?.campaign?.campaign_id, loadCampaigns, notify]);

  return {
    form,
    submitError,
    isSubmitting,
    launchResult,
    campaignState,
    loadCampaigns,
    handleFormChange,
    handleGenerateDesignator,
    handleLaunchSubmit,
    handleRetryMagentoGeneration,
  };
}
